<template>
  <div class="accFeed__wrapper matchmaking__wrapper subscriptions-page wide">
    <div class="accFeed__content matchmaking__content">
      <div class="market-project">
        <div class="offers">
          <div class="offers-status">
            <h3>You have <strong>{{ subscriptions.length }}</strong> product subscriptions:</h3>
          </div>
        </div>

        <SuccessMessage v-if="messageText" :text="messageText" />

        <div class="subscribe">
          <div v-if="isLoading" class="d-flex justify-content-center p-2">
            <div class="loader"></div>
          </div>

          <div v-else-if="!subscriptions.length" class="no-content">
            <p>You have <strong>no subscriptions</strong> yet.</p>
          </div>

          <div v-else class="accordion" id="accordionBuyerSubscribes">
            <div
              v-for="subscription in subscriptions"
              :key="subscription.id"
              class="subscribe__item"
            >
              <div class="subscribe__header" :id="subscription.headerId">
                <label class="subscribe__label mb-0">
                  <input
                    v-model="editForms[subscription.id].enabled"
                    type="checkbox"
                    class="subscribe__checkbox"
                  >
                  <span class="subscribe__custom-check"></span>
                  Marketplace:
                  <strong>
                    {{ subscription.marketplace.name }}, Country: {{ subscription.country.name || 'Any country' }}
                  </strong>
                </label>
                <button
                  class="btn-accordion"
                  :class="{ collapsed: expandedId !== subscription.id }"
                  type="button"
                  :aria-expanded="expandedId === subscription.id ? 'true' : 'false'"
                  :aria-controls="subscription.collapseId"
                  @click="toggleExpand(subscription.id)"
                >
                </button>
              </div>

              <div
                :id="subscription.collapseId"
                class="collapse"
                :class="{ show: expandedId === subscription.id }"
                :aria-labelledby="subscription.headerId"
              >
                <div class="subscribe__body">
                  <div class="subscribe__buttons">
                    <div class="subscribe-form-row">
                      <label>Marketplace</label>
                      <select
                        v-model="editForms[subscription.id].marketplace_id"
                        class="form-control"
                        @change="onEditMarketplaceChange(subscription.id)"
                      >
                        <option v-for="mp in marketplaces" :key="mp.id" :value="String(mp.id)">
                          {{ mp.name }}
                        </option>
                      </select>
                    </div>

                    <div class="subscribe-form-row">
                      <label>Country</label>
                      <select v-model="editForms[subscription.id].country_id" class="form-control">
                        <option value="">Any country</option>
                        <option
                          v-for="country in getEditCountryOptions(subscription.id)"
                          :key="country.id"
                          :value="String(country.id)"
                        >
                          {{ country.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="subscribe__buttons">
                    <a class="btn-action" href="#" @click.prevent="saveSubscription(subscription.id)">
                      Save subscription settings
                    </a>
                    <a
                      class="btn-action"
                      href="#"
                      @click.prevent="toggleSubscriptionContents(subscription)"
                    >
                      Display contents
                    </a>
                  </div>

                  <div v-if="contentsVisibleId === subscription.id" class="subscription-contents">
                    <div v-if="contentsLoadingId === subscription.id" class="d-flex justify-content-center p-2">
                      <div class="loader"></div>
                    </div>

                    <div v-else-if="!getSubscriptionContents(subscription.id).length" class="no-content">
                      <p><strong>No products</strong> match this subscription.</p>
                    </div>

                    <div v-else class="accordion offers__box subscription-contents__cards">
                      <BuyerProductCard
                        v-for="product in getSubscriptionContents(subscription.id)"
                        :key="`${subscription.id}-${product.id}`"
                        :product="product"
                        :accordion-id="`subscription-products-${subscription.id}`"
                        @saved="onProductSavedInContents(subscription.id, $event)"
                        @unsaved="onProductUnsavedInContents(subscription.id, $event)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MatchmakingService from '@/services/MatchmakingService';
import SuccessMessage from '@/components/matchmaking/SuccessMessage.vue';
import BuyerProductCard from '@/components/matchmaking/BuyerProductCard.vue';

export default {
  name: 'BuyerSubscriptions',
  components: {
    SuccessMessage,
    BuyerProductCard,
  },
  data() {
    return {
      expandedId: null,
      isLoading: false,
      messageText: '',
      messageTimer: null,
      subscriptions: [],
      marketplaces: [],
      editForms: {},
      contentsBySubscriptionId: {},
      contentsVisibleId: null,
      contentsLoadingId: null,
    };
  },
  async created() {
    await this.fetchMarketplaces();
    await this.fetchSubscriptions();
  },
  beforeDestroy() {
    if (this.messageTimer) clearTimeout(this.messageTimer);
  },
  methods: {
    showMessage(text) {
      this.messageText = text;
      if (this.messageTimer) clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => {
        this.messageText = '';
        this.messageTimer = null;
      }, 4000);
    },
    normalizeListPayload(payload) {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      if (payload && Array.isArray(payload.items)) return payload.items;
      return [];
    },
    ensureApiOk(responseData) {
      if (
        responseData
        && typeof responseData === 'object'
        && Number(responseData.status || 0) >= 400
      ) {
        throw new Error(responseData.message || 'Request failed');
      }
    },
    async fetchMarketplaces() {
      try {
        const { data } = await MatchmakingService.marketplaces();
        this.marketplaces = Array.isArray(data) ? data : [];
      } catch (e) {
        this.marketplaces = [];
      }
    },
    toSubscriptionVM(item) {
      const id = item?.id;
      return {
        id,
        marketplace: item?.marketplace || {},
        country: item?.country || {},
        headerId: `bp-head-${id}`,
        collapseId: `bp-collapse-${id}`,
      };
    },
    initEditForms() {
      const forms = {};
      (this.subscriptions || []).forEach((subscription) => {
        forms[subscription.id] = {
          marketplace_id: String(subscription.marketplace?.id || ''),
          country_id: String(subscription.country?.id || ''),
          enabled: true,
        };
      });
      this.editForms = forms;
    },
    async fetchSubscriptions() {
      this.isLoading = true;
      try {
        const { data } = await MatchmakingService.marketplaceProductSubscriptions();
        const list = this.normalizeListPayload(data);
        this.subscriptions = list.map(this.toSubscriptionVM);
        this.initEditForms();
      } catch (e) {
        this.subscriptions = [];
        this.editForms = {};
      } finally {
        this.isLoading = false;
      }
    },
    getMarketplaceCountries(marketplaceId) {
      if (!marketplaceId) return [];
      const mp = (this.marketplaces || [])
        .find((marketplace) => String(marketplace?.id) === String(marketplaceId));
      const raw = Array.isArray(mp?.filters?.countries)
        ? mp.filters.countries
        : (Array.isArray(mp?.countries) ? mp.countries : []);

      return raw
        .filter((country) => country && country.id !== undefined && country.id !== null)
        .map((country) => ({
          id: String(country.id),
          name: country.name || country.nicename || country.iso || String(country.id),
          iso: country.iso || '',
        }));
    },
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    onEditMarketplaceChange(subscriptionId) {
      if (!this.editForms[subscriptionId]) return;
      this.editForms[subscriptionId].country_id = '';
    },
    getEditCountryOptions(subscriptionId) {
      const marketplaceId = this.editForms[subscriptionId]?.marketplace_id;
      return this.getMarketplaceCountries(marketplaceId);
    },
    getSubscriptionContents(subscriptionId) {
      return this.contentsBySubscriptionId[String(subscriptionId)] || [];
    },
    productMatchesCountry(product, subscription) {
      const subscriptionCountry = subscription?.country || {};
      const subscriptionIso = String(subscriptionCountry.iso || '').trim().toLowerCase();
      const subscriptionName = String(subscriptionCountry.name || '').trim().toLowerCase();
      const productCountry = product?.company_country;

      if (!subscriptionIso && !subscriptionName) return true;

      if (productCountry && typeof productCountry === 'object') {
        const productIso = String(productCountry.iso || '').trim().toLowerCase();
        const productName = String(productCountry.name || '').trim().toLowerCase();
        return (
          (subscriptionIso && productIso === subscriptionIso)
          || (subscriptionName && productName === subscriptionName)
        );
      }

      const rawCountry = String(productCountry || '').trim().toLowerCase();
      return (
        (subscriptionIso && rawCountry === subscriptionIso)
        || (subscriptionName && rawCountry === subscriptionName)
      );
    },
    async loadSubscriptionContents(subscription) {
      const subscriptionId = String(subscription.id);
      const marketplaceId = Number(subscription?.marketplace?.id || 0);
      if (!marketplaceId) {
        this.contentsBySubscriptionId = {
          ...this.contentsBySubscriptionId,
          [subscriptionId]: [],
        };
        return;
      }

      this.contentsLoadingId = subscription.id;
      try {
        const { data } = await MatchmakingService.marketplaceProducts({
          user: 'buyer',
          marketplace_id: marketplaceId,
        });
        const list = this.normalizeListPayload(data);
        const filtered = list.filter((product) => this.productMatchesCountry(product, subscription));
        this.contentsBySubscriptionId = {
          ...this.contentsBySubscriptionId,
          [subscriptionId]: filtered,
        };
      } catch (e) {
        this.contentsBySubscriptionId = {
          ...this.contentsBySubscriptionId,
          [subscriptionId]: [],
        };
      } finally {
        this.contentsLoadingId = null;
      }
    },
    async toggleSubscriptionContents(subscription) {
      if (!subscription?.id) return;
      if (this.contentsVisibleId === subscription.id) {
        this.contentsVisibleId = null;
        return;
      }

      this.contentsVisibleId = subscription.id;
      const cached = this.getSubscriptionContents(subscription.id);
      if (Array.isArray(cached) && cached.length) return;
      await this.loadSubscriptionContents(subscription);
    },
    onProductSavedInContents(subscriptionId, product) {
      const key = String(subscriptionId);
      const items = this.contentsBySubscriptionId[key] || [];
      this.contentsBySubscriptionId = {
        ...this.contentsBySubscriptionId,
        [key]: items.map((item) => (
          String(item?.id) === String(product?.id)
            ? { ...item, saved: true }
            : item
        )),
      };
    },
    onProductUnsavedInContents(subscriptionId, product) {
      const key = String(subscriptionId);
      const items = this.contentsBySubscriptionId[key] || [];
      this.contentsBySubscriptionId = {
        ...this.contentsBySubscriptionId,
        [key]: items.map((item) => (
          String(item?.id) === String(product?.id)
            ? { ...item, saved: false }
            : item
        )),
      };
    },
    async saveSubscription(subscriptionId) {
      const form = this.editForms[subscriptionId];
      if (!form) return;

      if (!form.enabled) {
        await this.deleteSubscription(subscriptionId);
        return;
      }

      if (!form.marketplace_id) {
        this.showMessage('Please select marketplace.');
        return;
      }

      try {
        const payload = {
          id: Number(subscriptionId),
          marketplace_id: Number(form.marketplace_id),
        };

        if (form.country_id !== '' && form.country_id !== null && form.country_id !== undefined) {
          payload.country_id = Number(form.country_id);
        }

        const res = await MatchmakingService.updateMarketplaceProductSubscription(payload);
        this.ensureApiOk(res?.data);
        this.showMessage('Subscription updated');
        await this.fetchSubscriptions();
        this.contentsBySubscriptionId = {};
        this.contentsVisibleId = null;
      } catch (e) {
        this.showMessage(e?.message || 'Failed to update subscription');
      }
    },
    async deleteSubscription(subscriptionId) {
      try {
        const res = await MatchmakingService.deleteMarketplaceProductSubscription(subscriptionId);
        this.ensureApiOk(res?.data);
        this.showMessage('Subscription deleted');
        await this.fetchSubscriptions();
        const key = String(subscriptionId);
        if (this.contentsBySubscriptionId[key]) {
          const next = { ...this.contentsBySubscriptionId };
          delete next[key];
          this.contentsBySubscriptionId = next;
        }
        if (this.contentsVisibleId === subscriptionId) this.contentsVisibleId = null;
        if (this.expandedId === subscriptionId) this.expandedId = null;
      } catch (e) {
        this.showMessage(e?.message || 'Failed to delete subscription');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.subscriptions-page {
  grid-template-columns: 1fr !important;
}

.subscriptions-page .accFeed__content,
.subscriptions-page .matchmaking__content {
  width: 100%;
  max-width: 100%;
}

.subscriptions-page .market-project {
  min-height: 420px;
  padding-bottom: 24px;
}

.subscriptions-page .subscribe {
  background: transparent;
  padding: 0;
  margin: 0;
  border-radius: 0;
}

.subscriptions-page #accordionBuyerSubscribes,
.subscriptions-page .subscribe__item,
.subscriptions-page .subscribe__header,
.subscriptions-page .subscribe__body {
  width: 100%;
}

.subscriptions-page .subscribe__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-sizing: border-box;
}

.subscriptions-page .subscribe__header .subscribe__label {
  display: inline-flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}

.subscriptions-page .subscribe__body {
  box-sizing: border-box;
  padding: 20px 30px 24px;
}

.subscriptions-page .subscribe__body .subscribe-form-row {
  margin-bottom: 14px;
}

.offers-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.subscribe-form-row {
  margin-bottom: 12px;
}

.subscribe-form-row label {
  display: block;
  margin-bottom: 6px;
}

.subscribe__body.show {
  display: block;
}

.subscribe__buttons .btn-action {
  width: auto;
  min-width: 0;
  display: inline-flex;
}

.subscription-contents {
  margin-top: 12px;
}

.subscription-contents__cards .card {
  margin-bottom: 10px;
}

.subscription-contents__cards .card:last-child {
  margin-bottom: 0;
}
</style>
